/**
 * `ai-explanation` (12) FeatureModule — the in-app physician explanation panel alongside a signed
 * report. CLOUD-only (deployClass=cloud): its /api/v1 routes are not mounted on a local install, so the
 * screens degrade to a clean "not available on this install" state when the read probe 404s. The signed
 * compte-rendu is never blocked by the AI (AIX-0) — generation is best-effort and never in the clinical
 * path.
 *
 * Surfaces (under the authenticated AppShell; the router wraps each route in ProtectedRoute +
 * ErrorBoundary using requiredCapabilities):
 *   - /ai-explanation           — the by-id launcher (open a compte-rendu's explanation panel)
 *   - /ai-explanation/:crId      — the panel: read + bilingual FR/AR block + always-on disclaimer,
 *                                  generate / regenerate (per-action useCan), graceful indisponible.
 *
 * Capabilities are the server's EXACT keys (server/src/modules/ai-explanation/routes.js, Biologiste).
 * The module gates on ai.explication.read (the minimum to reach any surface); generate / regenerate are
 * gated per-control via useCan AND re-checked server-side every request.
 *
 * To register: add `import aiExplanationModule from "@/features/ai-explanation";` + the module to the
 * MODULES array in src/app/registry.ts (the ONLY router-side edit — features never touch the router).
 */
import type { FeatureModule } from "@/app";
import { AIX_CAPS, MODULE_CAPS } from "./caps";
import { registerAixMessages } from "./i18n";
import { ExplanationConsolePage } from "./screens/ExplanationConsolePage";
import { ExplanationPanelPage } from "./screens/ExplanationPanelPage";

const aiExplanationModule: FeatureModule = {
  id: "ai-explanation",
  title: "Explication IA",
  requiredCapabilities: MODULE_CAPS,
  routes: [
    { path: "/ai-explanation", element: <ExplanationConsolePage /> },
    { path: "/ai-explanation/:crId", element: <ExplanationPanelPage /> },
  ],
  nav: [
    {
      id: "ai-explanation",
      path: "/ai-explanation",
      label: "nav.aix.console",
      icon: "✦",
      order: 52,
      section: "nav.section.production",
      requiredCapabilities: [AIX_CAPS.read],
    },
  ],
  onRegister() {
    registerAixMessages();
  },
};

export default aiExplanationModule;
